import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  Fab,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import SaveIcon from "@material-ui/icons/Save";

import lodash from "lodash";
import { useSnackbar } from "notistack";
import React from "react";
import { useHistory } from "react-router";
import { Importer } from "../../components/CSV";
import { EmployeeTable } from "../../components/Employee";
import LocationFilter from "../../components/Filters/LocationFilter";
import { SearchField } from "../../components/Search";
import { ROUTES } from "../../constants";
import { SEARCH_EMPLOYEES } from "../../graphql/queries";
import { SearchEmployeesQuery } from "../../graphql/queries/searchEmployees";
import {
  SearchableEmployeeFilterInput,
  SearchEmployeesQueryVariables,
} from "../../graphql/types";
import useEmployeeFilters, { Filters } from "../../hooks/useEmployeeFilters";
import useSiteTitle from "../../hooks/useSiteTitle";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: "20px 0",
  },
  filter: { marginTop: 40, marginBottom: 20 },
  button: {
    margin: theme.spacing(1),
  },
}));
function generateFilter(
  state: Filters,
  searchTerm: string | undefined
): SearchableEmployeeFilterInput | undefined {
  const and: Array<SearchableEmployeeFilterInput> = [];
  const or: Array<SearchableEmployeeFilterInput> = [];

  if (state.locationId) {
    and.push({
      locationId: { eq: state.locationId },
    });
  }
  if (searchTerm) {
  }
  if (and.length === 0 && or.length === 0) {
    return undefined;
  }

  return {
    and,
  };
}
const Employees: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [searchValue, setSearchValue] = React.useState<string | undefined>("");
  const [showImport, setShowImport] = React.useState<boolean>(false);

  const {
    state,
    page: { page, rowsPerPage },
    setSelection,
    applieFilters,
    resetFilters,
    resetSelection,
    appliedState,
    setPage,
    setRowsPerPage,
  } = useEmployeeFilters();
  const { enqueueSnackbar } = useSnackbar();
  useSiteTitle("Testpersonendaten");
  const { data, loading, networkStatus, fetchMore, refetch } = useQuery<
    SearchEmployeesQuery,
    SearchEmployeesQueryVariables
  >(SEARCH_EMPLOYEES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...(state.filtersApplie && {
        filter: generateFilter(appliedState, searchValue),
      }),
      ...(searchValue && { search: searchValue }),
      limit: rowsPerPage,
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden der Testpersonendaten", {
        variant: "error",
      });
    },
  });
  const { searchEmployees } = data || {};
  const { items, total } = searchEmployees || {};

  const employees = React.useMemo(
    () =>
      (items || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page, rowsPerPage]
  );

  const _handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
    fetchMore({
      variables: {
        ...(state.filtersApplie && {
          filter: generateFilter(appliedState, searchValue),
        }),
        ...(searchValue && { search: searchValue }),
        from: newPage * rowsPerPage,
        limit: rowsPerPage,
      },
      updateQuery: (prev, { fetchMoreResult: { searchEmployees } = {} }) => {
        if (!searchEmployees) return prev;
        return Object.assign({}, prev, {
          searchEmployees: {
            ...prev.searchEmployees,
            items: lodash.uniqWith(
              (prev.searchEmployees.items || []).concat(
                searchEmployees.items || []
              ),
              lodash.isEqual
            ),
          },
        });
      },
    });
  };

  const _handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
  };

  const handleNavigate = (id: string) => {
    const [locationId, insuranceNumber] = id.split("#");
    history.push(`${ROUTES.EMPLOYEES}/${locationId}/${insuranceNumber}`);
  };
  const _handleClientSearch = lodash.debounce(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchValue(event.target.value);
    },
    500
  );

  const _handleCleareSearch = () => {
    setSearchValue("");
  };

  const _handleFinishImport = () => {
    refetch({
      ...(state.filtersApplie && {
        filter: generateFilter(appliedState, searchValue),
      }),
      ...(searchValue && { search: searchValue }),
      limit: rowsPerPage,
    });
    setShowImport(false);
  };

  const _onImport = () => {
    setShowImport((value) => !value);
  };
  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3">Testpersonendaten</Typography>
        <Tooltip title="Testperonsendaten einlesen">
          <Fab
            aria-label="create employee"
            size={"medium"}
            disabled={loading}
            onClick={_onImport}
            color={"primary"}
          >
            {!showImport ? <AddIcon /> : <CloseIcon />}
          </Fab>
        </Tooltip>
      </div>
      {showImport && <Importer onFinish={_handleFinishImport} />}
      <Grid container className={classes.filter}>
        <Grid item xs={12} md={12} container direction="row" spacing={2}>
          <Grid item>
            <Typography variant={"h6"}>Filter</Typography>
          </Grid>
          <Grid item xs={12} md={12} container justify="space-between">
            <Grid item xs={12} md={12} container justify="space-between">
              <LocationFilter
                onChange={setSelection("locationId")}
                onClear={resetSelection("locationId")}
                value={state.selected.locationId}
              />
              {state.changed && (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={applieFilters}
                  startIcon={<SaveIcon />}
                >
                  Speichern
                </Button>
              )}
              {state.filtersApplie && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={resetFilters}
                  className={classes.button}
                  startIcon={<RotateLeftIcon />}
                >
                  Zurücksetzten
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          container
          direction="row"
          alignItems="center"
          justify="flex-start"
          className={classes.header}
        >
          <SearchField
            onClearSearch={_handleCleareSearch}
            onSearchChange={_handleClientSearch}
            value={searchValue}
            hint="Die Suche erfolgt über die Felder Email, Nachname und
            Identifier Nr."
          />
       
        </Grid>
      </Grid>

      <EmployeeTable
        employees={employees || []}
        handleChangePage={_handleChangePage}
        handleChangeRowsPerPage={_handleChangeRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        loading={loading || networkStatus === NetworkStatus.fetchMore}
        totalEmployees={total || 0}
        onClickItem={handleNavigate}
      />
    </div>
  );
};

export default Employees;
